<template>
	<div class="result-container">
		<div class="swiper-container" ref="swiperContainer">
			<div class="swiper-wrapper">
				<div
					v-for="(template, index) in resultTemplates"
					:key="template.key + '-' + index"
					class="swiper-slide"
					
				>
				<div class="swiper-inner" :style="{ backgroundColor: template['colour'] }">
					<div
						v-for="(item, itemIndex) in template?.resultArray || []"
						:key="itemIndex"
						class="result-item"
					>
						<span class="result-label">{{ item.label }}：</span>
						<span
							v-if="!item.component && !item.render"
							class="result-value"
							:class="{ 'result-value-clickable': item.onClick }"
							:style="item.style || {}"
							@click="item.onClick && item.onClick(item.value)"
						>
							{{ item.value }}
						</span>
						<component
							v-else-if="item.component"
							:is="item.component"
							v-bind="item.componentProps || {}"
						/>
						<div
							v-else-if="item.render"
							v-html="item.render(fontColors)"
							@click="item?.onClick && item?.onClick(item.value)"
						/>
					</div>
					<div class="result-hint">{{ template['remark'] }}</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="resultTemplates.length > 1" class="result-pagination">
			<div class="pagination-track">
				<div 
					class="pagination-thumb"
					:style="thumbStyle"
				></div>
			</div>
		</div>
	</div>
</template>

<script>
import Swiper from 'swiper/js/swiper.js'
import 'swiper/css/swiper.css'

export default {
	name: 'Swiper',
	props: {
		resultTemplates: {
			type: Array,
			default: () => [],
		},
		bgColors: {
			type: Object,
			default: () => ({})
		},
		fontColors: {
			type: Object,
			default: () => ({})
		},
		hints: {
			type: Object,
			default: () => ({})
		},
	},
	computed: {
		// 指示器样式
		thumbStyle() {
			const trackWidth = 35
			const pageCount = this.resultTemplates.length
			if (pageCount <= 1) return {}
			
			const thumbWidth = (trackWidth - 2) / pageCount
			const left = this.currentIndex * thumbWidth
			
			return {
				transform: `translateX(${left}px)`,
				width: `${thumbWidth}px`
			}
		}
	},
	data() {
		return {
			mySwiper: null,
			currentIndex: 0,
			isInitialized: false,
		}
	},
	watch: {
		resultTemplates: {
			deep: true,
			handler() {
				this.$nextTick(() => {
					this.reInitSwiper()
				})
			}
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.initSwiper()
		})
	},
	beforeUnmount() {
		this.destroySwiper()
	},
	methods: {
		destroySwiper() {
			this.isInitialized = false
			if (this.mySwiper) {
				this.mySwiper.destroy()
				this.mySwiper = null
			}
		},
		reInitSwiper() {
			this.destroySwiper()
			this.$nextTick(() => {
				this.initSwiper()
			})
		},
		// 初始化swiper
		initSwiper() {
			const container = this.$refs.swiperContainer
			if (!container) return

			const pageCount = this.resultTemplates.length
			if (pageCount <= 1) {
				this.currentIndex = 0
				this.isInitialized = true
				return
			}

			const self = this
			this.mySwiper = new Swiper(container, {
				direction: 'horizontal',
				loop: true,
				slidesPerView: 1,
				slidesPerGroup: 1,
				spaceBetween: 0,
				// 元素发生变化的时候
				observer: true,
				observeParents: true,
				on: {
					slideChange: function() {
						/**
						 * this.realIndex 是当前显示的 slide 的索引。this是Swiper 实例！
						 * realIndex 是 Swiper 库内置的属性，当启用 loop: true 时（第146行），Swiper 会自动提供这个属性。
						 * self.currentIndex = this.realIndex 这行代码不可以删除
						 * */ 
						self.currentIndex = this.realIndex
						self.$emit('slideChange', self.currentIndex)
					}
				}
			})

			this.isInitialized = true
		},
		getBgColor(keyChain) {
			if (!Array.isArray(keyChain) || keyChain.length === 0) return ''
			return keyChain.reduce((obj, key) => obj?.[key], this.bgColors)
		},
		getHint(keyChain) {
			if (!Array.isArray(keyChain) || keyChain.length === 0) return ''
			return keyChain.reduce((obj, key) => obj?.[key], this.hints)
		},
		handleSlideChange(index) {
			this.currentIndex = index
			this.$emit('slideChange', index)
		}
	},
}
</script>

<style lang="less" scoped>
.result-container {
	width: 100%;
	box-sizing: border-box;
	overflow: hidden;
	position: relative;
}

.swiper-container {
	width: 100%;
	overflow: hidden;
	box-sizing: border-box;
}

.swiper-wrapper {
	display: flex;
}

.swiper-slide {
	width: 100%;
}
.swiper-inner {
	width: 100%;
	flex-shrink: 0;
	box-sizing: border-box;
	padding: 12px;
	border-radius: 6px;
	min-height: 108px;
}
.result-item {
	display: flex;
	align-items: flex-start;
	font-size: 12px;
	line-height: 18px;
	margin-bottom: 8px;

	&:last-child {
		margin-bottom: 0;
	}
}

.result-label {
	color: #5C606A;
	white-space: nowrap;
	flex-shrink: 0;
	min-width: 52px;
}

.result-value {
	color: #1A1D25;
	flex: 1;
	word-break: break-all;

	&.result-value-clickable {
		color: #2290FD;
		cursor: pointer;
		text-decoration: underline;
	}
}

.result-hint {
	font-size: 10px;
	line-height: 16px;
	color: #5C606A;
	margin-top: 8px;
}

.result-pagination {
	margin-top: 8px;
	display: flex;
	justify-content: center;
	align-items: center;

	.pagination-track {
		width: 35px;
		height: 5px;
		background-color: rgba(26, 29, 37, 0.1);
		border-radius: 10px;
		padding: 1px;
		box-sizing: border-box;

		.pagination-thumb {
			height: 3px;
			background-color: #FFFFFF;
			border-radius: 1.5px;
			transition: transform 0.3s ease;
		}
	}
}
</style>
